
import React, { useState, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet";
import { useGuides } from "@/hooks/use-guides";
import { Guide } from "@/types/guides";
import { GuidePreview } from "@/components/guides/GuidePreview";
import { supabase } from "@/integrations/supabase/client"; 
import { GuideDownloadForm } from "@/components/guides/GuideDownloadForm";
import { Session } from "@supabase/supabase-js";
import { GuideHeader } from "@/components/guides/GuideHeader";
import { GuideList } from "@/components/guides/GuideList";
import { GuideDownloadHandler } from "@/components/guides/GuideDownloadHandler";
import { useLocation, useNavigate } from "react-router-dom";

export default function GuidePage() {
  const { guides, isLoading } = useGuides();
  const [session, setSession] = useState<Session | null>(null);
  const [previewGuide, setPreviewGuide] = useState<Guide | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const openDownloadModal = queryParams.get('openDownloadModal') === 'true';
  const guideSlug = queryParams.get('guideSlug');
  
  // Default cover image for guides without covers
  const defaultCoverImage = "/lovable-uploads/0af25d51-4e55-4b75-8948-ba6ef7e018b7.png";

  // Fetch session on component mount
  useEffect(() => {
    async function getSession() {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    }
    
    getSession();
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
      }
    );
    
    return () => subscription.unsubscribe();
  }, []);

  // Get download handling logic from our custom handler
  const { handleDownload, downloadFormGuide, setDownloadFormGuide } = GuideDownloadHandler({ session });

  // Handle guide download request from URL parameters
  useEffect(() => {
    if (openDownloadModal && guideSlug && guides.length > 0) {
      // Find the guide by slug
      const guideToDownload = guides.find(g => g.slug === guideSlug);
      
      if (guideToDownload) {
        // Open download modal
        setDownloadFormGuide(guideToDownload);
        
        // Clean up URL without reloading the page
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      } else if (guideSlug) {
        // If we have a specific guide slug but couldn't find it, redirect to its detail page
        navigate(`/guides/${guideSlug}?openDownloadModal=true`);
      }
    }
  }, [guides, guideSlug, openDownloadModal, navigate, setDownloadFormGuide]);

  // Check for guide download request from session storage (for backward compatibility)
  useEffect(() => {
    const openDownloadModal = sessionStorage.getItem('openDownloadModal');
    const downloadGuideId = sessionStorage.getItem('downloadGuideId');
    
    if (openDownloadModal === 'true' && downloadGuideId && guides.length > 0) {
      // Find the guide by ID
      const guideToDownload = guides.find(g => g.id === downloadGuideId);
      
      if (guideToDownload) {
        setDownloadFormGuide(guideToDownload);
        // Clear the session storage flags
        sessionStorage.removeItem('openDownloadModal');
        sessionStorage.removeItem('downloadGuideId');
      }
    }
  }, [guides, setDownloadFormGuide]);

  const handlePreview = (e: React.MouseEvent, guide: Guide) => {
    e.preventDefault();
    setPreviewGuide(guide);
  };

  return (
    <Layout>
      <Helmet>
        <title>Resources & Guides | Grandpal</title>
      </Helmet>
      
      <div className="bg-gradient-to-b from-white to-grandpal-beige/20 py-16 md:py-24">
        <div className="container max-w-7xl">
          <GuideHeader />
          
          <GuideList 
            guides={guides}
            isLoading={isLoading}
            defaultCoverImage={defaultCoverImage}
            onPreview={handlePreview}
            onDownload={handleDownload}
          />
        </div>
      </div>

      {previewGuide && (
        <GuidePreview 
          guide={previewGuide} 
          onClose={() => setPreviewGuide(null)} 
        />
      )}

      {downloadFormGuide && (
        <GuideDownloadForm
          guide={downloadFormGuide}
          open={!!downloadFormGuide}
          onOpenChange={(open) => !open && setDownloadFormGuide(null)}
        />
      )}

      <div className="w-full bg-white py-16 md:py-24">
        <img 
          src="/lovable-uploads/47f2aef4-6ba9-49f0-a0ee-dd0d0dc05675.png" 
          alt="Flower Activity" 
          className="w-full max-h-[400px] object-contain"
        />
      </div>
    </Layout>
  );
}
